import React from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  TooltipProps,
} from 'recharts';
import { TrendingUp, Loader2 } from 'lucide-react';

/**
 * Interface for dung collection data point
 */
export interface DungCollectionData {
  date: string;
  weight: number;
  status?: 'collected' | 'pending' | 'processing';
}

interface DungCollectionChartProps {
  data: DungCollectionData[];
  loading?: boolean;
  error?: string | null;
  height?: number;
}

/**
 * Custom tooltip component for better UX
 */
const CustomTooltip: React.FC<TooltipProps<number, string>> = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
        <p className="font-semibold text-gray-700">{label}</p>
        <p className="text-green-600">
          Weight: <span className="font-bold">{payload[0].value} kg</span>
        </p>
        {payload[0].payload.status && (
          <p className="text-sm text-gray-500 capitalize">
            Status: {payload[0].payload.status}
          </p>
        )}
      </div>
    );
  }
  return null;
};

/**
 * DungCollectionChart - Line chart showing daily dung collection trend over 30 days
 *
 * Features:
 * - Displays daily collection amounts in kilograms
 * - Green/blue color scheme for organic/natural theme
 * - Interactive tooltips with status information
 * - Responsive design for mobile and desktop
 * - Loading and error states
 *
 * @param data - Array of daily collection data points
 * @param loading - Loading state indicator
 * @param error - Error message if data fetch failed
 * @param height - Chart height in pixels (default: 350)
 */
const DungCollectionChart: React.FC<DungCollectionChartProps> = ({
  data,
  loading = false,
  error = null,
  height = 350,
}) => {
  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center" style={{ height }}>
        <Loader2 className="w-8 h-8 animate-spin text-green-600" />
        <span className="ml-2 text-gray-600">Loading chart data...</span>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="flex items-center justify-center" style={{ height }}>
        <div className="text-center">
          <p className="text-red-600 font-semibold">Failed to load chart</p>
          <p className="text-sm text-gray-500 mt-1">{error}</p>
        </div>
      </div>
    );
  }

  // Empty state
  if (!data || data.length === 0) {
    return (
      <div className="flex items-center justify-center" style={{ height }}>
        <div className="text-center text-gray-500">
          <TrendingUp className="w-12 h-12 mx-auto mb-2 opacity-50" />
          <p>No collection data available</p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full">
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-gray-800 flex items-center">
          <TrendingUp className="w-5 h-5 mr-2 text-green-600" />
          Daily Dung Collection Trend
        </h3>
        <p className="text-sm text-gray-500 mt-1">
          Tracking collection amounts over the last 30 days
        </p>
      </div>

      <ResponsiveContainer width="100%" height={height}>
        <LineChart
          data={data}
          margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
          <XAxis
            dataKey="date"
            stroke="#666"
            tick={{ fontSize: 12 }}
            tickMargin={10}
          />
          <YAxis
            stroke="#666"
            tick={{ fontSize: 12 }}
            label={{ value: 'Weight (kg)', angle: -90, position: 'insideLeft' }}
          />
          <Tooltip content={<CustomTooltip />} />
          <Legend
            wrapperStyle={{ paddingTop: '20px' }}
            iconType="line"
          />
          <Line
            type="monotone"
            dataKey="weight"
            name="Dung Collected (kg)"
            stroke="#10b981"
            strokeWidth={2}
            dot={{ fill: '#10b981', r: 4 }}
            activeDot={{ r: 6, fill: '#059669' }}
            animationDuration={1000}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

export default DungCollectionChart;
